import { getI18nLanguageStorage } from "../../shared/storageUtils";
import { DEFAULT_LOCALE, Locales, SUPPORTED_LANGUAGES } from "./constants";

const langCodeToLocale: Record<string, Locales> = {
  es: Locales.ES_PY,
  pt: Locales.PT_BR,
};

/**
 * Parses a locale string and returns a supported locale.
 *
 * If the provided locale is supported, it is returned as-is.
 * Otherwise, attempts to extract the language code from the locale string
 * and map it to a supported locale using `langCodeToLocale`. If no mapping is found,
 * returns the default locale.
 *
 * @param locale - The locale string to parse (e.g., "en-US", "pt-BR").
 * @returns The supported locale string.
 */
export function parseLocale(locale: string): Locales {
  if (isSupportedLanguage(locale)) {
    return locale;
  }

  const targetLocale = locale ?? DEFAULT_LOCALE;
  const langCode = targetLocale.split("-")[0];
  return langCodeToLocale[langCode] ?? DEFAULT_LOCALE;
}

/**
 * Checks if the provided language locale is supported.
 *
 * @param langLocale - The language locale string to check.
 * @returns `true` if `langLocale` is a supported language, otherwise `false`.
 */
export function isSupportedLanguage(
  langLocale: string | null | undefined
): langLocale is Locales {
  return (
    langLocale != null && Object.keys(SUPPORTED_LANGUAGES).includes(langLocale)
  );
}

/**
 * Retrieves the user's preferred locale.
 *
 * Attempts to obtain the locale from local storage.
 * If the stored locale is supported, it is returned.
 * If local storage is inaccessible or the stored locale is unsupported, falls back to the browser's locale.
 *
 * @returns {Locales} The user's locale code.
 */
export function getUserLocale(): Locales {
  try {
    const storedLangLocale = getI18nLanguageStorage();
    if (isSupportedLanguage(storedLangLocale)) {
      return storedLangLocale;
    }
  } catch (error) {
    console.warn("Could not access localStorage:", error);
  }

  const browserLocale = navigator.language ?? navigator.languages[0];
  const parsedLocale = parseLocale(browserLocale);
  return parsedLocale;
}
